import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Trophy,
  Activity,
  TrendingUp,
  Users,
  AlertCircle,
  BarChart3,
  Target,
  Shield,
  Zap,
  Clock,
  DollarSign,
  ChevronRight,
  Info,
  Heart,
  Wind
} from 'lucide-react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import advancedAnalytics from '../services/advancedAnalytics'
import sportsDataService from '../services/sportsDataService'

const GameDetails = () => {
  const { gameId } = useParams()
  const [game, setGame] = useState(null)
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    loadGameData()
  }, [gameId])

  const loadGameData = async () => {
    setLoading(true)
    try {
      // In production, fetch the specific game
      // For now, using mock data with comprehensive analytics
      const mockGame = {
        id: gameId,
        homeTeam: {
          name: 'Golden State Warriors',
          abbreviation: 'GSW',
          logo: 'https://a.espncdn.com/i/teamlogos/nba/500/gs.png',
          record: '25-15',
          stats: {
            wins: 25,
            losses: 15,
            winPct: 62.5,
            offensiveRating: 115.2,
            defensiveRating: 108.5,
            pace: 101.3,
            threePTPercent: 0.385,
            threePointAttemptRate: 0.42,
            paintPointsPerGame: 44.5,
            fastBreakPoints: 14.2,
            turnoverRate: 13.2,
            reboundRate: 51.2,
            freeThrowRate: 0.23,
            assistRatio: 19.5,
            last10: '7-3',
            homeRecord: '15-5',
            awayRecord: '10-10',
            atsHome: '12-8',
            daysRest: 2
          }
        },
        awayTeam: {
          name: 'Los Angeles Lakers',
          abbreviation: 'LAL',
          logo: 'https://a.espncdn.com/i/teamlogos/nba/500/lal.png',
          record: '22-18',
          stats: {
            wins: 22,
            losses: 18,
            winPct: 55.0,
            offensiveRating: 112.8,
            defensiveRating: 111.2,
            pace: 99.8,
            threePTPercent: 0.362,
            threePointAttemptRate: 0.38,
            paintPointsPerGame: 52.3,
            fastBreakPoints: 11.5,
            turnoverRate: 14.5,
            reboundRate: 49.8,
            freeThrowRate: 0.28,
            assistRatio: 17.2,
            last10: '5-5',
            homeRecord: '14-6',
            awayRecord: '8-12',
            atsAway: '9-11',
            daysRest: 1
          }
        }
      }

      setGame(mockGame)

      // Get comprehensive analytics
      const analysis = await advancedAnalytics.getCompleteGameAnalysis(
        gameId,
        mockGame.homeTeam,
        mockGame.awayTeam
      )
      setAnalytics(analysis)
    } catch (error) {
      console.error('Error loading game data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading comprehensive analytics...</p>
        </div>
      </div>
    )
  }

  if (!game || !analytics) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400">Game not found</p>
      </div>
    )
  }

  const { winProbability, pace, playStyle, keyMetrics, injuries, h2h, trends, matchupAdvantages, bettingInsights, playerImpact } = analytics

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Game Header */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Link to="/games" className="text-gray-400 hover:text-white flex items-center gap-2">
            ← Back to Games
          </Link>
          <span className="text-sm text-gray-400">
            Comprehensive Game Analysis
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <img src={game.awayTeam.logo} alt={game.awayTeam.name} className="h-20 w-20 mx-auto mb-2" />
            <h3 className="text-xl font-bold text-white">{game.awayTeam.name}</h3>
            <p className="text-gray-400">{game.awayTeam.record}</p>
          </div>

          <div className="text-center flex flex-col justify-center">
            <p className="text-gray-400 text-sm mb-2">Win Probability</p>
            <div className="flex justify-center gap-4">
              <div>
                <p className="text-3xl font-bold text-white">
                  {(winProbability.away * 100).toFixed(1)}%
                </p>
                <p className="text-xs text-gray-400">Away</p>
              </div>
              <div className="text-gray-400">vs</div>
              <div>
                <p className="text-3xl font-bold text-white">
                  {(winProbability.home * 100).toFixed(1)}%
                </p>
                <p className="text-xs text-gray-400">Home</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 mt-2">
              Confidence: {winProbability.confidence}%
            </p>
          </div>

          <div className="text-center">
            <img src={game.homeTeam.logo} alt={game.homeTeam.name} className="h-20 w-20 mx-auto mb-2" />
            <h3 className="text-xl font-bold text-white">{game.homeTeam.name}</h3>
            <p className="text-gray-400">{game.homeTeam.record}</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 border-b border-dark-border">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'matchup', label: 'Matchup', icon: Target },
          { id: 'players', label: 'Players', icon: Users },
          { id: 'injuries', label: 'Injuries', icon: Heart },
          { id: 'trends', label: 'Trends', icon: TrendingUp },
          { id: 'statistical', label: 'Statistical Analysis', icon: DollarSign },
          { id: 'h2h', label: 'H2H', icon: Activity }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Predictions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-dark-card border border-dark-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                <h3 className="font-medium text-white">Winner Prediction</h3>
              </div>
              <p className="text-2xl font-bold text-white">
                {winProbability.home > 0.5 ? game.homeTeam.abbreviation : game.awayTeam.abbreviation}
              </p>
              <p className="text-sm text-gray-400">
                {Math.max(winProbability.home, winProbability.away) * 100}% probability
              </p>
            </div>

            <div className="bg-dark-card border border-dark-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-5 w-5 text-blue-500" />
                <h3 className="font-medium text-white">Game Pace</h3>
              </div>
              <p className="text-2xl font-bold text-white">
                {pace.tempoRating}
              </p>
              <p className="text-sm text-gray-400">
                {pace.expectedPace.toFixed(1)} possessions
              </p>
            </div>

            <div className="bg-dark-card border border-dark-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-green-500" />
                <h3 className="font-medium text-white">Total Points</h3>
              </div>
              <p className="text-2xl font-bold text-white">
                {pace.totalsPrediction.projected}
              </p>
              <p className="text-sm text-gray-400">
                {pace.totalsPrediction.home} - {pace.totalsPrediction.away}
              </p>
            </div>
          </div>

          {/* Play Style Analysis */}
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Play Style Analysis</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-white mb-2">{game.awayTeam.abbreviation}</h3>
                <p className="text-primary-500 font-medium mb-2">{playStyle.away.primary}</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">3PT Rate</span>
                    <span className="text-white">{playStyle.away.threePointRate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Paint Points</span>
                    <span className="text-white">{playStyle.away.paintPoints}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Fast Break</span>
                    <span className="text-white">{playStyle.away.fastBreakPoints}</span>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs text-gray-400 mb-1">Strengths</p>
                  <div className="flex flex-wrap gap-1">
                    {playStyle.away.strengths.map((s, i) => (
                      <span key={i} className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-white mb-2">{game.homeTeam.abbreviation}</h3>
                <p className="text-primary-500 font-medium mb-2">{playStyle.home.primary}</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">3PT Rate</span>
                    <span className="text-white">{playStyle.home.threePointRate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Paint Points</span>
                    <span className="text-white">{playStyle.home.paintPoints}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Fast Break</span>
                    <span className="text-white">{playStyle.home.fastBreakPoints}</span>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs text-gray-400 mb-1">Strengths</p>
                  <div className="flex flex-wrap gap-1">
                    {playStyle.home.strengths.map((s, i) => (
                      <span key={i} className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Four Factors */}
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Four Factors of Basketball</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'eFG%', away: keyMetrics.fourFactors.away.eFG, home: keyMetrics.fourFactors.home.eFG },
                { label: 'TOV%', away: keyMetrics.fourFactors.away.turnoverRate, home: keyMetrics.fourFactors.home.turnoverRate, inverse: true },
                { label: 'REB%', away: keyMetrics.fourFactors.away.reboundRate, home: keyMetrics.fourFactors.home.reboundRate },
                { label: 'FT Rate', away: keyMetrics.fourFactors.away.freeThrowRate, home: keyMetrics.fourFactors.home.freeThrowRate }
              ].map(factor => (
                <div key={factor.label} className="text-center">
                  <p className="text-gray-400 text-sm mb-2">{factor.label}</p>
                  <div className="space-y-1">
                    <p className={`text-lg font-bold ${
                      factor.inverse
                        ? factor.away < factor.home ? 'text-green-500' : 'text-white'
                        : factor.away > factor.home ? 'text-green-500' : 'text-white'
                    }`}>
                      {factor.away}
                    </p>
                    <p className="text-gray-400 text-xs">vs</p>
                    <p className={`text-lg font-bold ${
                      factor.inverse
                        ? factor.home < factor.away ? 'text-green-500' : 'text-white'
                        : factor.home > factor.away ? 'text-green-500' : 'text-white'
                    }`}>
                      {factor.home}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'matchup' && (
        <div className="space-y-6">
          {/* Matchup Advantages */}
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Key Matchup Advantages</h2>
            <div className="space-y-3">
              {matchupAdvantages.map((adv, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      adv.impact === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                      adv.impact === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {adv.impact}
                    </span>
                    <div>
                      <p className="text-white font-medium">{adv.type}</p>
                      <p className="text-sm text-gray-400">{adv.description}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              ))}
            </div>
          </div>

          {/* Radar Chart Comparison */}
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Team Comparison</h2>
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={[
                { stat: 'Offense', away: 75, home: 82 },
                { stat: 'Defense', away: 70, home: 78 },
                { stat: '3PT', away: 72, home: 85 },
                { stat: 'Rebounding', away: 68, home: 75 },
                { stat: 'Pace', away: 65, home: 72 },
                { stat: 'FT', away: 78, home: 70 }
              ]}>
                <PolarGrid stroke="#374151" />
                <PolarAngleAxis dataKey="stat" stroke="#9ca3af" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="#374151" />
                <Radar name={game.awayTeam.abbreviation} dataKey="away" stroke="#f43f5e" fill="#f43f5e" fillOpacity={0.3} />
                <Radar name={game.homeTeam.abbreviation} dataKey="home" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {activeTab === 'players' && (
        <div className="space-y-6">
          {/* Key Players Impact */}
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Key Players Impact</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Away Team Players */}
              <div>
                <h3 className="text-lg font-medium text-white mb-3">{game.awayTeam.name}</h3>
                {playerImpact.away.keyPlayers.map((player, i) => (
                  <div key={i} className="mb-4 p-3 bg-gray-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">{player.name}</p>
                      <span className={`text-xs px-2 py-1 rounded ${
                        player.impact === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {player.impact}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-gray-400 text-xs">PPG</p>
                        <p className="text-white font-bold">{player.ppg}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">APG</p>
                        <p className="text-white font-bold">{player.apg}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">RPG</p>
                        <p className="text-white font-bold">{player.rpg}</p>
                      </div>
                    </div>
                    {player.vsOpponent && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <p className="text-xs text-gray-400">vs {game.homeTeam.abbreviation}</p>
                        <p className="text-sm text-white">{player.vsOpponent.last5Games}</p>
                      </div>
                    )}
                  </div>
                ))}
                <div className="p-3 bg-gray-800/30 rounded-lg">
                  <p className="text-sm text-gray-400">Bench Impact</p>
                  <p className="text-white font-medium">Grade: {playerImpact.away.benchImpact.rating}</p>
                  <p className="text-xs text-gray-400">{playerImpact.away.benchImpact.scoringPerGame} PPG</p>
                </div>
              </div>

              {/* Home Team Players */}
              <div>
                <h3 className="text-lg font-medium text-white mb-3">{game.homeTeam.name}</h3>
                {playerImpact.home.keyPlayers.map((player, i) => (
                  <div key={i} className="mb-4 p-3 bg-gray-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">{player.name}</p>
                      <span className={`text-xs px-2 py-1 rounded ${
                        player.impact === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {player.impact}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-gray-400 text-xs">PPG</p>
                        <p className="text-white font-bold">{player.ppg}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">APG</p>
                        <p className="text-white font-bold">{player.apg}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">RPG</p>
                        <p className="text-white font-bold">{player.rpg}</p>
                      </div>
                    </div>
                    {player.vsOpponent && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <p className="text-xs text-gray-400">vs {game.awayTeam.abbreviation}</p>
                        <p className="text-sm text-white">{player.vsOpponent.last5Games}</p>
                      </div>
                    )}
                  </div>
                ))}
                <div className="p-3 bg-gray-800/30 rounded-lg">
                  <p className="text-sm text-gray-400">Bench Impact</p>
                  <p className="text-white font-medium">Grade: {playerImpact.home.benchImpact.rating}</p>
                  <p className="text-xs text-gray-400">{playerImpact.home.benchImpact.scoringPerGame} PPG</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'injuries' && (
        <div className="space-y-6">
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Injury Report & Impact</h2>

            {injuries.keyInsights.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                  <p className="text-yellow-400 font-medium">Key Insights</p>
                </div>
                {injuries.keyInsights.map((insight, i) => (
                  <p key={i} className="text-sm text-gray-300">{insight}</p>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-white mb-3">{game.awayTeam.name}</h3>
                {injuries.away.length === 0 ? (
                  <p className="text-gray-400">No significant injuries</p>
                ) : (
                  injuries.away.map((injury, i) => (
                    <div key={i} className="p-3 bg-gray-800/50 rounded-lg mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-white font-medium">{injury.player}</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          injury.status === 'Out' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {injury.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">{injury.injury}</p>
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <p className="text-xs text-gray-400">Team Without Player</p>
                        <p className="text-white">{injury.statsWithout.record}</p>
                        <p className="text-xs text-gray-400">
                          Win Prob Impact: {(injury.winProbChange * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div>
                <h3 className="text-lg font-medium text-white mb-3">{game.homeTeam.name}</h3>
                {injuries.home.length === 0 ? (
                  <p className="text-gray-400">No significant injuries</p>
                ) : (
                  injuries.home.map((injury, i) => (
                    <div key={i} className="p-3 bg-gray-800/50 rounded-lg mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-white font-medium">{injury.player}</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          injury.status === 'Out' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {injury.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">{injury.injury}</p>
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <p className="text-xs text-gray-400">Team Without Player</p>
                        <p className="text-white">{injury.statsWithout.record}</p>
                        <p className="text-xs text-gray-400">
                          Win Prob Impact: {(injury.winProbChange * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-4 p-4 bg-gray-800/30 rounded-lg">
              <p className="text-sm text-gray-400">Total Injury Impact on Game</p>
              <p className="text-2xl font-bold text-white">
                {injuries.totalImpact > 0 ? '+' : ''}{(injuries.totalImpact * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-gray-400">
                Adjusted Win Probability: {(injuries.adjustedWinProb * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'trends' && (
        <div className="space-y-6">
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Performance Trends & Patterns</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Away Team Trends */}
              <div>
                <h3 className="text-lg font-medium text-white mb-3">{game.awayTeam.name}</h3>

                <div className="space-y-3">
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-400 mb-2">Performance vs Expectations</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400">Overall:</span>
                        <span className="text-white ml-2">{trends.away.ats.overall}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Away:</span>
                        <span className="text-white ml-2">{trends.away.ats.away}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Last 10:</span>
                        <span className="text-white ml-2">{trends.away.ats.last10}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">As Dog:</span>
                        <span className="text-white ml-2">{trends.away.ats.asUnderdog}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-400 mb-2">Totals O/U</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400">Over:</span>
                        <span className="text-white ml-2">{trends.away.totals.over}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Under:</span>
                        <span className="text-white ml-2">{trends.away.totals.under}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Last 10:</span>
                        <span className="text-white ml-2">{trends.away.totals.last10}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Avg:</span>
                        <span className="text-white ml-2">{trends.away.totals.avgActual}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-400 mb-2">Situational</p>
                    {trends.away.situational.map((sit, i) => (
                      <p key={i} className="text-sm text-white">{sit}</p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Home Team Trends */}
              <div>
                <h3 className="text-lg font-medium text-white mb-3">{game.homeTeam.name}</h3>

                <div className="space-y-3">
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-400 mb-2">Performance vs Expectations</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400">Overall:</span>
                        <span className="text-white ml-2">{trends.home.ats.overall}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Home:</span>
                        <span className="text-white ml-2">{trends.home.ats.home}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Last 10:</span>
                        <span className="text-white ml-2">{trends.home.ats.last10}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">As Fav:</span>
                        <span className="text-white ml-2">{trends.home.ats.asFavorite}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-400 mb-2">Totals O/U</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400">Over:</span>
                        <span className="text-white ml-2">{trends.home.totals.over}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Under:</span>
                        <span className="text-white ml-2">{trends.home.totals.under}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Last 10:</span>
                        <span className="text-white ml-2">{trends.home.totals.last10}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Avg:</span>
                        <span className="text-white ml-2">{trends.home.totals.avgActual}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-400 mb-2">Situational</p>
                    {trends.home.situational.map((sit, i) => (
                      <p key={i} className="text-sm text-white">{sit}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'statistical' && (
        <div className="space-y-6">
          {/* Statistical Insights */}
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Statistical Pattern Analysis</h2>

            {/* Statistical Consensus Analysis */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">Point Differential Analysis</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Public Expectation</span>
                    <span className="text-white">{bettingInsights.publicMoney.spread.away}% {game.awayTeam.abbreviation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Model Prediction</span>
                    <span className="text-green-400">{bettingInsights.sharpMoney.spread.home}% {game.homeTeam.abbreviation}</span>
                  </div>
                  {bettingInsights.sharpMoney.spread.home > bettingInsights.publicMoney.spread.home && (
                    <p className="text-xs text-yellow-400 mt-1">⚠️ Model favors {game.homeTeam.abbreviation}</p>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-800/50 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">Win Probability</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Public Opinion</span>
                    <span className="text-white">{bettingInsights.publicMoney.moneyline.away}% {game.awayTeam.abbreviation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Statistical Model</span>
                    <span className="text-green-400">{bettingInsights.sharpMoney.moneyline.home}% {game.homeTeam.abbreviation}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-800/50 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">Total Points Projection</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Public View</span>
                    <span className="text-white">{bettingInsights.publicMoney.total.over}% High-Scoring</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Analytics Model</span>
                    <span className="text-green-400">{bettingInsights.sharpMoney.total.under}% Low-Scoring</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistical Trends */}
            <div className="p-4 bg-gray-800/50 rounded-lg mb-6">
              <p className="text-sm text-gray-400 mb-3">Statistical Movement Trends</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400">Point Differential Trend</p>
                  <p className="text-white">
                    {bettingInsights.lineMovement.spread.open} → {bettingInsights.lineMovement.spread.current}
                    <span className="text-sm text-gray-400 ml-2">
                      ({bettingInsights.lineMovement.spread.direction})
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Total Points Trend</p>
                  <p className="text-white">
                    {bettingInsights.lineMovement.total.open} → {bettingInsights.lineMovement.total.current}
                    <span className="text-sm text-gray-400 ml-2">
                      ({bettingInsights.lineMovement.total.direction})
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Analytical Model Predictions */}
            <div>
              <p className="text-sm text-gray-400 mb-3">Model-Based Analysis</p>
              <div className="space-y-3">
                {bettingInsights.systemPicks.map((pick, i) => (
                  <div key={i} className="p-4 bg-gray-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">{pick.system}</p>
                      <span className={`text-sm px-2 py-1 rounded ${
                        pick.confidence >= 70 ? 'bg-green-500/20 text-green-400' :
                        pick.confidence >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {pick.confidence}% confidence
                      </span>
                    </div>
                    <p className="text-primary-500 font-medium">{pick.pick}</p>
                    <p className="text-sm text-gray-400 mt-1">{pick.reasoning}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'h2h' && (
        <div className="space-y-6">
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Head-to-Head History</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <p className="text-gray-400 text-sm">Last 10</p>
                <p className="text-2xl font-bold text-white">
                  {h2h.last10Meetings.homeWins}-{h2h.last10Meetings.awayWins}
                </p>
                <p className="text-xs text-gray-400">{game.homeTeam.abbreviation} leads</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-sm">Avg Margin</p>
                <p className="text-2xl font-bold text-white">{h2h.last10Meetings.avgPointDiff}</p>
                <p className="text-xs text-gray-400">points</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-sm">Avg Total</p>
                <p className="text-2xl font-bold text-white">{h2h.last10Meetings.avgTotal}</p>
                <p className="text-xs text-gray-400">points</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-sm">This Season</p>
                <p className="text-2xl font-bold text-white">
                  {h2h.thisseason.homeWins}-{h2h.thisseason.awayWins}
                </p>
                <p className="text-xs text-gray-400">{h2h.thisseason.meetings} games</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">Recent Meetings</p>
                {h2h.thisseason.scores.map((score, i) => (
                  <p key={i} className="text-white">{score}</p>
                ))}
              </div>

              <div className="p-3 bg-gray-800/50 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">Historical Trends</p>
                {h2h.trends.map((trend, i) => (
                  <p key={i} className="text-white text-sm">{trend}</p>
                ))}
              </div>

              <div className="p-3 bg-gray-800/50 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">Dominant Performers</p>
                <div className="space-y-1">
                  <p className="text-white text-sm">{h2h.dominantPlayers.home}</p>
                  <p className="text-white text-sm">{h2h.dominantPlayers.away}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Insights Summary */}
      <div className="bg-gradient-to-r from-primary-500/10 to-blue-500/10 border border-primary-500/30 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">🎯 Quick Betting Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-400">Best Bet</p>
            <p className="text-white font-medium">
              {winProbability.home > 0.55 ? `${game.homeTeam.abbreviation} ML` : `${game.awayTeam.abbreviation} +pts`}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Total</p>
            <p className="text-white font-medium">
              {pace.tempoRating === 'FAST' ? 'Over' : 'Under'} {pace.totalsPrediction.projected}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Key Factor</p>
            <p className="text-white font-medium">
              {matchupAdvantages[0]?.description || 'Balanced matchup'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Confidence</p>
            <p className="text-white font-medium">
              {winProbability.confidence}% High
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GameDetails